import { RowWithId } from '@/components/table/types/RowWithId.ts';
import {
  BulkAction,
  BulkActionContext,
} from '@/components/table/types/BulkAction.ts';
import ActionButton from '@/components/buttons/ActionButton.tsx';

/** `BulkAction.variant` still speaks the old button's vocabulary. */
const actionTones = {
  default: 'accent',
  secondary: 'secondary',
  outline: 'outline',
  danger: 'danger',
} as const;

interface BulkActionButtonProps<T extends RowWithId> {
  action: BulkAction<T>;
  rows: T[];
  ctx: BulkActionContext;
  /** Some action is in flight — the whole bar locks until it resolves. */
  isBarBusy: boolean;
  isRunning: boolean;
  onRun: (action: BulkAction<T>) => void;
}

/**
 * A single action in the selection bar. An action with `render` gets no
 * wrapper mechanics — it opts out of confirm/loading handling by definition.
 *
 * The tooltip sits on a wrapping span, not on the button: a disabled button
 * swallows pointer events, so `title` on it would never show.
 */
const BulkActionButton = <T extends RowWithId>({
  action,
  rows,
  ctx,
  isBarBusy,
  isRunning,
  onRun,
}: BulkActionButtonProps<T>) => {
  if (action.render) {
    return <span>{action.render(rows, ctx)}</span>;
  }

  const isDisabled = action.disabled?.(rows) ?? false;
  const Icon = action.icon;

  return (
    <span title={isDisabled ? action.disabledTooltip?.(rows) : undefined}>
      <ActionButton
        tone={actionTones[action.variant ?? 'secondary']}
        size="xs"
        onClick={() => onRun(action)}
        disabled={isDisabled || isBarBusy}
        isLoading={isRunning}
      >
        {Icon && <Icon />}
        {action.label}
      </ActionButton>
    </span>
  );
};

export default BulkActionButton;
